import learningMaterial from 'ApiService/Teacher/learningMaterial';
import Card from 'components/card';
import { useFormKeyboardSubmit } from 'pages/Hooks/useStepperKeyboardNavigation';
import { useState } from 'react';
import toast from 'react-hot-toast';
import AddInfo from './Steps/AddInfo';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const Index = () => {
  const [resetFlag, setResetFlag] = useState(false);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    courseId: '',
    batchId: '',
    batchStructureId: '',
    days: [
      {
        lectureDay: 1,
        topic: '',
        subtopics: [
          {
            title: '',
            materials: [
              {
                type: 'content',
                content: '',
                file: null,
                fileUrl: '',
              },
            ],
          },
        ],
      },
    ],
  });
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConfirmSubmit = () => {
    const isValid =
      formData.courseId &&
      formData.batchId &&
      formData.days.length > 0 &&
      formData.days.every(
        (day) =>
          day.topic &&
          day.subtopics.length > 0 &&
          day.subtopics.every((sub) => {
            if (!sub.title) return false;

            if (!sub.materials || sub.materials.length === 0) return false;

            return sub.materials.some((mat) => {
              if (mat.type === 'content') {
                return !!mat.content;
              } else {
                return !!mat.file || !!mat.fileUrl;
              }
            });
          })
      );
    const daysSet = new Set(formData.days.map((d) => d.lectureDay));

    if (daysSet.size !== formData.days.length) {
      toast.error('Duplicate lecture days not allowed');
      return;
    }
    if (isValid) {
      setIsModalOpenConfirmation(true);
    } else {
      toast.error('Please complete all fields properly.');
    }
  };

  async function handleSubmit() {
    if (loading) return;

    if (formData.courseId && formData.batchId && formData.days.length > 0) {
      setLoading(true);
      const formDataToSend = new FormData();

      formDataToSend.append('courseId', formData.courseId);
      formDataToSend.append('batchId', formData.batchId);
      formDataToSend.append('batchStructureId', formData.batchStructureId);

      formData.days.forEach((day, dayIndex) => {
        formDataToSend.append(`days[${dayIndex}][lectureDay]`, day.lectureDay);

        formDataToSend.append(`days[${dayIndex}][topic]`, day.topic);

        day.subtopics.forEach((sub, subIndex) => {
          formDataToSend.append(
            `days[${dayIndex}][subtopics][${subIndex}][title]`,
            sub.title
          );
          sub.materials.forEach((mat, matIndex) => {
            formDataToSend.append(
              `days[${dayIndex}][subtopics][${subIndex}][materials][${matIndex}][type]`,
              mat.type
            );

            if (mat.type === 'content') {
              formDataToSend.append(
                `days[${dayIndex}][subtopics][${subIndex}][materials][${matIndex}][content]`,
                mat.content
              );
            } else {
              if (mat.file) {
                formDataToSend.append(
                  `days[${dayIndex}][subtopics][${subIndex}][materials][${matIndex}][file]`,
                  mat.file
                );
              } else if (mat.fileUrl) {
                formDataToSend.append(
                  `days[${dayIndex}][subtopics][${subIndex}][materials][${matIndex}][fileUrl]`,
                  mat.fileUrl
                );
              }
            }
          });
        });
      });
      try {
        if (!formDataToSend.has('courseId')) {
          toast.error('Invalid form data');
          return;
        }
        const response = await learningMaterial.createTrainingMaterial(
          formDataToSend
        );
        if (response.data.success) {
          setResetFlag((prev) => !prev); // 🔥 trigger reset
          setIsModalOpenConfirmation(false);
          setFormData({
            courseId: '',
            batchId: '',
            days: [
              {
                lectureDay: 1,
                topic: '',
                subtopics: [
                  {
                    title: '',
                    materials: [
                      {
                        type: 'content',
                        content: '',
                        file: null,
                        fileUrl: '',
                      },
                    ],
                  },
                ],
              },
            ],
          });
          toast.success(response?.data?.message);
        }
      } catch (error) {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('All fields are required.');
    }
  }
  useFormKeyboardSubmit(handleSubmit, disable, loading);
  return (
    <div className="mt-3 h-full w-full">
      <>
        <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />

        <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3  3xl:w-7/12">
          <Card
            extra={
              ' -mt-[280px] w-full md:-mt-[240px] md:px-[70px] h-full mx-auto pb-3'
            }
          >
            <div className="rounded-[20px]">
              <AddInfo
                setLoading={setLoading}
                setDisable={setDisable}
                handleInputChange={handleInputChange}
                formData={formData}
                setFormData={setFormData}
                resetFlag={resetFlag}
              />
            </div>
            {disable && (
              <p className="mt-2 text-sm text-red-500">
                Please complete all required fields
              </p>
            )}

            <div className="mt-4 flex flex-col justify-end px-3 pt-2 md:flex-row md:px-8">
              <button
                disabled={disable}
                onClick={handleConfirmSubmit}
                className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
              >
                {loading ? 'Loading..' : 'Upload'}
              </button>
            </div>
          </Card>
        </div>
      </>
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => setIsModalOpenConfirmation(false)}
        onConfirm={handleSubmit}
        title="Confirm Upload of Training Material"
        message="Are you sure you want to upload this training material?"
        confirmText={loading ? 'Uploading...' : 'Yes, Upload Material'}
        cancelText="Cancel"
        icon="info"
        disableConfirm={loading}
      />
    </div>
  );
};

export default Index;
